import Phaser from "phaser";
import { SceneKey, assertTransition } from "../flow/game-flow";
import {
  HUB_META,
  hubProtos,
  primaryProto,
  protoDisplayTitle,
  shareUrlFor,
  toolProtos,
  type ProtoDef,
} from "../flow/proto-registry";
import { sharedInput } from "../input/input-controller";
import { getUiLayout } from "../ui/layout";
import { clearOverlay } from "../ui/screen-overlay";
import { markActiveScene } from "../ui/screen-state";

/**
 * Prototype hub — Prototypes (play) + Tools (Art Lab) on one page.
 */
export class TitleScene extends Phaser.Scene {
  private disposed = false;

  constructor() {
    super(SceneKey.Title);
  }

  create(): void {
    this.disposed = false;
    markActiveScene(SceneKey.Title);
    sharedInput.attach();
    clearOverlay();
    this.buildMenuDom();

    if (typeof document !== "undefined") {
      document.title = HUB_META.title;
    }

    const { width, height } = this.scale;
    this.add.rectangle(width / 2, height / 2, width, height, 0x121a2e).setDepth(-1);
    this.add
      .text(width / 2, height * 0.12, HUB_META.title, {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "34px",
        color: "#ffd27a",
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height * 0.175, HUB_META.subtitle, {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "14px",
        color: "#9aa3c7",
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height * 0.92, `v${HUB_META.version}`, {
        fontFamily: "Malgun Gothic, sans-serif",
        fontSize: "12px",
        color: "#6f7694",
      })
      .setOrigin(0.5);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardownMenuDom());
    this.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardownMenuDom());
  }

  update(): void {
    if (this.disposed) return;
    const pressed = sharedInput.consumePressed();
    if (pressed.confirm) {
      const primary = primaryProto();
      if (primary) this.startProto(primary);
    }
  }

  private startProto(proto: ProtoDef): void {
    if (this.disposed) return;
    assertTransition(SceneKey.Title, proto.scene);
    this.teardownMenuDom();
    this.scene.start(proto.scene);
  }

  private sectionHtml(
    title: string,
    sectionClass: string,
    protos: readonly ProtoDef[],
    copyTitle: string,
  ): string {
    if (!protos.length) return "";
    const rows = protos
      .map((p) => {
        const label = this.escapeHtml(protoDisplayTitle(p));
        const primary = p.primary ? " menu-btn-primary" : "";
        return `
          <div class="menu-row" data-row="${p.id}">
            <button type="button" class="menu-btn${primary}" data-proto="${p.id}">${label}</button>
            <button type="button" class="menu-copy" data-copy="${p.id}" title="${copyTitle}">링크</button>
          </div>`;
      })
      .join("");
    return `
      <section class="menu-section ${sectionClass}">
        <h2 class="menu-section-title">${title}</h2>
        ${rows}
      </section>`;
  }

  private buildMenuDom(): void {
    const ui = getUiLayout();
    ui.sync();
    ui.clearShell();
    const frame = ui.frame;
    frame.style.display = "flex";
    frame.style.flexDirection = "column";
    frame.style.alignItems = "center";
    frame.style.justifyContent = "center";
    frame.style.pointerEvents = "auto";
    ui.overlay.style.pointerEvents = "auto";

    const panel = document.createElement("div");
    panel.className = "menu-panel";
    const copyTitle = "Notion용 entry 링크 복사";
    const play = hubProtos();
    const tools = toolProtos();
    const html =
      this.sectionHtml("Prototypes", "menu-section-play", play, copyTitle) +
      this.sectionHtml("Tools", "menu-section-tools", tools, copyTitle);
    panel.innerHTML =
      html || `<p class="menu-empty">prototypes.json에 항목이 없습니다.</p>`;
    frame.appendChild(panel);

    for (const p of [...play, ...tools]) {
      panel
        .querySelector(`[data-proto="${p.id}"]`)
        ?.addEventListener("click", () => this.startProto(p));
      panel
        .querySelector(`[data-copy="${p.id}"]`)
        ?.addEventListener("click", (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          void this.copyShareLink(p.id, ev.currentTarget as HTMLButtonElement);
        });
    }
  }

  private async copyShareLink(
    entryId: string,
    btn: HTMLButtonElement,
  ): Promise<void> {
    const url = shareUrlFor(entryId);
    const ok = await writeClipboard(url);
    const prev = btn.textContent;
    btn.textContent = ok ? "복사됨" : "실패";
    btn.classList.toggle("menu-copy-ok", ok);
    window.setTimeout(() => {
      if (this.disposed) return;
      btn.textContent = prev || "링크";
      btn.classList.remove("menu-copy-ok");
    }, 1400);
  }

  private escapeHtml(s: string): string {
    return s
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  private teardownMenuDom(): void {
    this.disposed = true;
    clearOverlay();
  }
}

async function writeClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
